import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, AlertCircle, Check } from 'lucide-react';
import changePasswordService from '../../../services/changePasswordService';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsSuccess(false);
    setIsLoading(true);

    if (!identificationNumber.trim()) {
      setMessage('Por favor, introduce tu número de identificación.');
      setIsLoading(false);
      return;
    }

    try {
      const data = await changePasswordService.requestVerificationCode(identificationNumber);

      if (data.success) {
        setMessage('Se ha enviado un código de verificación a tu WhatsApp.');
        setIsSuccess(true);
        setIdentificationNumber(''); // Limpiar el campo
      } else {
        setMessage(data.message || 'Número de identificación no encontrado.');
        setIsSuccess(false);
      }
    } catch (error) {
      console.error('Error requesting verification code:', error);
      setMessage('Error de conexión. Inténtalo de nuevo.');
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full relative"
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            ¿Olvidaste tu Contraseña?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Introduce tu número de identificación y te enviaremos un código por WhatsApp para restablecer tu contraseña.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="forgot-identification" className="sr-only">Número de Identificación</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="forgot-identification"
                  value={identificationNumber}
                  onChange={(e) => setIdentificationNumber(e.target.value)}
                  placeholder="Número de identificación"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all duration-300"
                  required
                />
              </div>
            </div>

            {message && (
              <motion.div
                className={`p-3 rounded-xl flex items-center gap-3 ${
                  isSuccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {isSuccess ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                <p className="text-sm font-medium">{message}</p>
              </motion.div>
            )}

            <motion.button
              type="submit"
              className="w-full text-white py-3 px-6 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
              style={{
                backgroundColor: '#9e187e',
                backgroundImage: 'linear-gradient(135deg, #9e187e 0%, #7d1463 100%)'
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <>
                  Enviar Código
                  <User className="w-5 h-5" />
                </>
              )}
            </motion.button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ForgotPasswordModal;